// src/auth/EmailVerification.js
import React, { useState, useEffect } from 'react';
import { 
  auth, 
  db, 
  sendEmailVerification, 
  doc, 
  updateDoc 
} from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { CheckCircle, Mail, AlertTriangle, AlertCircle, Inbox } from 'lucide-react';

const EmailVerification = () => {
  const { currentUser, isAuthenticated } = useAuth();
  const [verificationSent, setVerificationSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    // Check if a cooldown is stored in localStorage
    const storedCooldown = localStorage.getItem('verificationCooldown');
    const cooldownTime = storedCooldown ? parseInt(storedCooldown, 10) : 0;
    
    if (cooldownTime > Date.now()) {
      setCooldown(Math.ceil((cooldownTime - Date.now()) / 1000));
    }
  }, []);

  useEffect(() => {
    // Set up a timer to count down the cooldown
    if (cooldown > 0) {
      const timer = setTimeout(() => {
        setCooldown(cooldown - 1);
      }, 1000);
      
      return () => clearTimeout(timer);
    } else if (cooldown === 0) {
      // Clear the localStorage entry when cooldown reaches 0
      localStorage.removeItem('verificationCooldown');
    }
  }, [cooldown]);

  // Redirect to dashboard if the user is already verified
  if (currentUser?.emailVerified) {
    // Update the verified status in Firestore
    updateDoc(doc(db, "users", currentUser.uid), {
      verified: true
    }).catch(error => {
      console.error("Error updating verification status:", error);
    });
    
    return <Navigate to="/dashboard" />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  const sendVerificationEmail = async () => {
    setLoading(true);
    setError('');
    
    try {
      console.log("Attempting to send verification email to:", currentUser.email);
      console.log("User verified status:", currentUser.emailVerified);
      
      await sendEmailVerification(auth.currentUser);
      console.log("Verification email sent successfully");
      setVerificationSent(true);
      
      // Set a 60-second cooldown
      const cooldownTime = Date.now() + 60000; // 60 seconds from now
      localStorage.setItem('verificationCooldown', cooldownTime.toString());
      setCooldown(60);
    } catch (error) {
      console.error("Error sending verification email:", error);
      console.error("Error details:", error.code, error.message);
      setError(`Failed to send verification email: ${error.message}. Please try again later.`);
    } finally {
      setLoading(false);
    }
  };

  const checkVerificationStatus = async () => {
    setLoading(true);
    
    try {
      // Reload the user to get updated verification status
      await auth.currentUser.reload();
      
      if (auth.currentUser.emailVerified) {
        // Update Firestore
        await updateDoc(doc(db, "users", auth.currentUser.uid), {
          verified: true
        });
        
        console.log("User verified successfully!");
        
        // Force a reload of the page to trigger the redirect
        window.location.reload();
      } else {
        setError('Your email is not verified yet. Please check your inbox and click the verification link.');
      }
    } catch (error) {
      console.error("Error checking verification status:", error);
      setError('Failed to check verification status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Mail className="h-12 w-12 text-blue-500" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          Verify your email
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          We've sent a verification link to {currentUser?.email}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {/* Spam folder notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <Inbox className="h-5 w-5 text-blue-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">Important Note</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Firebase verification emails <strong>often go to spam folders</strong>. 
                  Please check your spam/junk folder if you don't see the email in your inbox.
                </p>
              </div>
            </div>
          </div>
          
          {verificationSent ? (
            <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <CheckCircle className="h-5 w-5 text-green-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-700">
                    Verification email sent! Please check your inbox <strong>and spam folder</strong>.
                  </p>
                </div>
              </div>
            </div>
          ) : null}
          
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex flex-col space-y-4">
            <p className="text-sm text-gray-600">
              Please click the verification link in the email we sent to activate your account.
              If you don't see it in your inbox, <strong>be sure to check your spam/junk folder</strong> as verification 
              emails are frequently filtered.
            </p>
            
            <button
              onClick={checkVerificationStatus}
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Checking...' : 'I\'ve verified my email'}
            </button>
            
            <button
              onClick={sendVerificationEmail}
              disabled={loading || cooldown > 0}
              className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {cooldown > 0 
                ? `Resend verification email (${cooldown}s)` 
                : loading 
                  ? 'Sending...' 
                  : 'Resend verification email'
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;